/**
 * SubscriptionExpired — Premium SaaS full-content-area screen.
 *
 * Rendered inside the existing DashboardLayout shell (sidebar + header stay
 * visible). Replaces the Outlet content when subscription.is_active === false.
 *
 * Layout:
 *   Desktop → two columns: [left: message + CTAs] [right: benefits card]
 *   Mobile  → single column stacked
 */

const SUPPORT_TEL = 'tel:+918160108049';

// ─── Inline SVG Icons ────────────────────────────────────────────────────────

function PhoneIcon({ size = 18 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function HeadsetIcon({ size = 22 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
    </svg>
  );
}

function CheckIcon({ size = 14 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ─── Decorative SVG blobs ─────────────────────────────────────────────────────

function RightBlob() {
  return (
    <svg
      aria-hidden="true"
      className="absolute top-0 right-0 opacity-[0.18] pointer-events-none"
      width="180"
      height="160"
      viewBox="0 0 180 160"
      fill="none"
    >
      <ellipse cx="140" cy="30" rx="100" ry="80" fill="#50ab3d" />
      <ellipse cx="170" cy="90" rx="60" ry="50" fill="#50ab3d" />
    </svg>
  );
}

function LeftBlob() {
  return (
    <svg
      aria-hidden="true"
      className="absolute -top-[30px] -right-[30px] opacity-[0.09] pointer-events-none"
      width="260"
      height="220"
      viewBox="0 0 260 220"
      fill="none"
    >
      <ellipse cx="190" cy="60" rx="130" ry="100" fill="#50ab3d" />
    </svg>
  );
}

// ─── Benefits data ────────────────────────────────────────────────────────────

const BENEFITS = [
  { text: 'Restore full access', sub: 'Get back to all features instantly' },
  { text: 'Continue your work', sub: 'Pick up right where you left off' },
  { text: 'Uninterrupted service', sub: 'Zero downtime for your team' },
];

// ─── Main Component ───────────────────────────────────────────────────────────

export function SubscriptionExpired() {
  return (
    <div className="w-full h-full min-h-full flex flex-col overflow-y-auto overflow-x-hidden p-3 sm:p-5 md:p-7 box-border bg-gradient-to-br from-[#f4faf4] via-[#f8f9fa] to-[#f0f8f0]">
      {/* ── Responsive two-column wrapper ── */}
      <div className="w-full max-w-[960px] m-auto flex flex-col lg:flex-row gap-3 sm:gap-5 items-stretch">
        {/* ════════════════════════════════════
            LEFT COLUMN — message + CTAs
            ════════════════════════════════════ */}
        <div className="relative flex flex-col items-start bg-white rounded-[16px] sm:rounded-[20px] border border-[#e2ede2] p-5 sm:p-7 md:p-8 lg:p-11 lg:flex-1 lg:min-w-0 overflow-hidden box-border w-full shadow-sm">
          <LeftBlob />

          {/* Orange SUBSCRIPTION EXPIRED badge */}
          <div className="inline-flex items-center gap-[6px] bg-[#ea580c]/10 border border-[#ea580c]/20 rounded-full px-3 py-1 mb-4 sm:mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ea580c] inline-block shrink-0" />
            <span className="text-[10px] sm:text-[11px] font-bold tracking-widest text-[#ea580c] uppercase">
              Subscription Expired
            </span>
          </div>

          {/* Main heading */}
          <h1 className="m-0 mb-2 sm:mb-4 text-[18px] sm:text-[22px] md:text-[26px] lg:text-[clamp(26px,3.2vw,38px)] font-extrabold leading-[1.2] tracking-[-0.025em] text-[#0d1f0d]">
            Your Subscription{' '}
            <span className="text-[#50ab3d]">Has Expired</span>
          </h1>

          {/* Description */}
          <p className="m-0 mb-5 sm:mb-8 text-[13px] sm:text-[15px] leading-[1.6] sm:leading-[1.7] text-gray-600 max-w-[430px]">
            Your Aexon subscription has expired. Access to your account is
            currently unavailable. Please renew your subscription to continue
            using Aexon&apos;s services.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-2.5 sm:gap-3 mb-5 sm:mb-8 w-full sm:w-auto">
            {/* Secondary: Contact Support */}
            <a
              href={SUPPORT_TEL}
              id="sub-expired-contact-btn"
              className="flex w-full sm:inline-flex sm:w-auto justify-center items-center gap-2 bg-white text-[#0d1f0d] border-2 border-gray-300 rounded-xl px-4 py-2.5 sm:px-[22px] sm:py-[13px] text-[13px] sm:text-[14px] font-semibold no-underline whitespace-nowrap transition-all duration-150 ease-in hover:-translate-y-[1px] hover:border-[#50ab3d] hover:text-[#50ab3d] box-border"
            >
              <PhoneIcon size={15} />
              Contact Support
            </a>
          </div>

          {/* Support info strip */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 sm:gap-3.5 bg-[#50ab3d]/5 border border-[#50ab3d]/15 rounded-[12px] sm:rounded-2xl p-3 sm:px-[18px] sm:py-[14px] w-full box-border">
            {/* Headset in soft green circle */}
            <div className="shrink-0 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-[#50ab3d]/10 flex items-center justify-center text-[#50ab3d]">
              <HeadsetIcon size={18} className="sm:w-[22px] sm:h-[22px]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="m-0 text-[12px] sm:text-[13px] font-bold text-[#0d1f0d] leading-[1.4]">
                Need help?
              </p>
              <p className="m-0 mt-0.5 text-[11px] sm:text-[12px] text-gray-500 leading-[1.5]">
                Our support team is here to assist you with subscription
                renewal.
              </p>
            </div>
          </div>
        </div>

        {/* ════════════════════════════════════
            RIGHT COLUMN — benefits card
            ════════════════════════════════════ */}
        <div className="relative flex flex-col shrink-0 w-full lg:w-[300px] bg-white rounded-[16px] sm:rounded-[20px] border border-[#e2ede2] p-4 sm:p-6 md:p-7 lg:px-7 lg:py-9 overflow-hidden box-border shadow-sm">
          <RightBlob />
          {/* Subtle diagonal pattern */}
          <div
            aria-hidden="true"
            className="absolute inset-0 rounded-[16px] sm:rounded-[20px] pointer-events-none"
            style={{
              backgroundImage:
                'repeating-linear-gradient(135deg, rgba(80,171,61,0.035) 0px, rgba(80,171,61,0.035) 1px, transparent 1px, transparent 28px)',
            }}
          />

          <div className="relative z-10 flex flex-col h-full">
            {/* Pill label */}
            <div className="inline-flex items-center self-start bg-[#50ab3d]/10 rounded-full px-2.5 py-1 mb-3 sm:mb-4.5">
              <span className="text-[9px] sm:text-[10px] font-bold text-[#50ab3d] uppercase tracking-[0.08em]">
                Why Renew?
              </span>
            </div>

            {/* Card heading */}
            <h2 className="m-0 mb-2.5 sm:mb-3.5 text-[16px] sm:text-[18px] md:text-[20px] font-extrabold leading-[1.25] text-[#0d1f0d] tracking-[-0.01em]">
              Keep Your Business{' '}
              <span className="text-[#50ab3d]">Running Smoothly</span>
            </h2>

            {/* Green accent bar */}
            <div className="w-[32px] sm:w-[38px] h-[3px] rounded-sm bg-[#50ab3d] mb-4 sm:mb-6" />

            {/* Benefits */}
            <ul className="m-0 p-0 list-none flex flex-col gap-3 sm:gap-4.5">
              {BENEFITS.map(({ text, sub }) => (
                <li key={text} className="flex items-start gap-2.5 sm:gap-3">
                  <div className="shrink-0 mt-0.5 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#50ab3d]/10 flex items-center justify-center text-[#50ab3d]">
                    <CheckIcon size={12} className="sm:w-[14px] sm:h-[14px]" />
                  </div>
                  <div>
                    <p className="m-0 text-[12px] sm:text-[14px] font-bold text-[#0d1f0d] leading-[1.3]">
                      {text}
                    </p>
                    <p className="m-0 mt-0.5 sm:mt-[3px] text-[11px] sm:text-[12px] text-gray-500 leading-[1.5]">
                      {sub}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionExpired;
